import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Drawer,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow
} from '@material-ui/core';
import { getCustomerAmastyQuotesApi, getQuoteProProductsApi } from 'api';
import useLocalStorageTableCells from 'hooks/useLocalStorageTableCells';
import CustomTableToolbar from 'pages/quotes/QuoteProgression/CustomTableToolbar';
import FilterDrawer from 'pages/quotes/QuoteProgression/FilterDrawer';
import { useStyles } from '../styles';
import RecentQuotesTableRow from './RecentQuotesTableRow';
import { recentQuotesTableCells } from './helpers';


interface IRecentQuotesTableProps {
  email?: string;
  searchFields?: { field: string; name: string }[];
}

interface IFilterConditions {
  stage?: string;
  startDate?: string;
  endDate?: string;
}

const RecentQuotesTable = ({ email, searchFields }: IRecentQuotesTableProps) => {
  const classes = useStyles();
  const emailCondition = {
    field: 'customer_email',
    value: email,
    conditionType: 'eq'
  };

  const baseSearchParams = {
    sortDirection: 'desc',
    sortField: 'created_at',
    pageSize: 10,
    currentPage: 1,
    filterConditions: email ? [emailCondition] : [],
    searchConditions: []
  };
  const [filterConditions, setFilterConditions] = useState<IFilterConditions>({});
  const [, setSearchConditions] = useState({});
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const { userStoreData: {storeCode} } = useSelector((state: IReduxState) => state.user);
  const isCustomer3 = storeCode === 'customer3';

  const [searchParams, setSearchParams] = useState(baseSearchParams);
  const dispatch = useDispatch();
  const storageKey = email ? 'recentQuotesHistoryTableCells' : 'quoteProgressionTableCells';
  const { headTableCells: recentHeadTableCells, setHeadTableCells: setRecentHeadTableCells } = useLocalStorageTableCells(storageKey, recentQuotesTableCells);
  const { allQuoteProducts } = useSelector((state: IReduxState) => state.quote);

  useEffect(() => {
    if (!allQuoteProducts.length) {
      getQuoteProProductsApi({ dispatch });
    }
  }, []);

  useEffect(() => {
    const condition = [];

    if (filterConditions.stage) {
      condition.push({
        field: 'status',
        value: filterConditions.stage,
        conditionType: 'eq'
      });
    }

    if (filterConditions.startDate) {
      condition.push({
        field: 'created_at',
        value: filterConditions.startDate,
        conditionType: 'gteq'
      });
    }

    if (filterConditions.endDate) {
      condition.push({
        field: 'created_at',
        value: filterConditions.endDate,
        conditionType: 'lteq'
      });
    }


    getCustomerAmastyQuotesApi({
      dispatch,
      ...searchParams,
      filterConditions: [...searchParams.filterConditions, ...condition],
    });
  }, [JSON.stringify(searchParams), JSON.stringify(filterConditions)]);

  const handleChangePage = (_, newPage) => {
    setSearchParams({
      ...searchParams,
      currentPage: newPage + 1,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setSearchParams({
      ...searchParams,
      pageSize: parseInt(event.target.value, 10),
      currentPage: 1,
    });
  };

  const onSearch = (searchValue, searchField) => {
    const condition = [];

    if (email) {
      condition.push(emailCondition);
    }

    if (searchValue.length) {
      condition.push({
        field: searchField,
        value: `%${searchValue}%`,
        conditionType: 'like'
      });
    }

    setSearchParams((params) => {
      return {
        ...params,
        currentPage: 1,
        filterConditions: condition,
      };
    });
    setSearchConditions(searchValue.length ? condition : {});
  };

  const { customerAmastyQuotes, customerAmastyQuotesCount } = useSelector((state: IReduxState) => state.quote);

  return (
    <>
      <div className="table-container">
        <TableContainer component={Paper}>
          <CustomTableToolbar
            onSearch={onSearch}
            setOpenFilter={setOpenFilter}
            filterConditions={filterConditions}
            recentHeadTableCells={recentHeadTableCells}
            setRecentHeadTableCells={setRecentHeadTableCells}
            searchFields={searchFields}
            storageKey={storageKey}
          />
          <Table aria-label="collapsible table" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                {recentHeadTableCells?.map((cell, index) => {
                  if (!cell.display) {
                    return null;
                  }

                  return (
                    <TableCell
                      key={index}
                      align="center"
                      className={`min-w-${cell.width}`}
                    >
                      {cell.label}
                    </TableCell>
                  );
                })}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!customerAmastyQuotes?.length ?
                <TableRow>
                  <TableCell colSpan={Object.keys(recentHeadTableCells).length + 1} align="center">
                    {`No matching ${isCustomer3 ? 'customer' : 'patient'} activity found.`}
                  </TableCell>
                </TableRow>
                :
                customerAmastyQuotes
                  .map((quote, idx) => (
                    <RecentQuotesTableRow
                      key={idx}
                      quote={quote}
                      headTableCells={recentHeadTableCells}
                    />
                  ))
              }
            </TableBody>
          </Table>
        </TableContainer>
        {!!customerAmastyQuotes?.length &&
          <TablePagination
            rowsPerPageOptions={[10, 20, 40, 80, 100]}
            component="div"
            className={classes.paginationContainer}
            count={customerAmastyQuotesCount}
            rowsPerPage={searchParams.pageSize}
            page={searchParams.currentPage - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        }
        <Drawer
          anchor="right"
          open={openFilter}
          onClose={() => setOpenFilter((openFilter) => !openFilter)}
          PaperProps={{ style: { maxWidth: '400px' } }}
        >
          <FilterDrawer
            setOpenFilter={setOpenFilter}
            setFilterConditions={setFilterConditions}
            setCurrentPage={() => {
              setSearchParams({
                ...searchParams,
                currentPage: 1,
              });
            }}
            filterConditions={filterConditions}
          />
        </Drawer>
      </div>
    </>
  );
};

export default RecentQuotesTable;
